import { useEffect, useRef, useState, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const WS_URL = BACKEND_URL.replace(/^http/, 'ws').replace(/^https/, 'wss');

export function useWebSocket({ onMessage, enabled = true }) {
  const ws = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimeout = useRef(null);
  const pingInterval = useRef(null);

  const connect = useCallback(() => {
    if (!enabled) return;

    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('[WebSocket] No token found, skipping connection');
      return;
    }

    try {
      const wsUrl = `${WS_URL}/api/ws?token=${token}`;
      console.log('[WebSocket] Connecting to:', wsUrl.replace(token, '***'));
      
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log('[WebSocket] Connected successfully');
        setIsConnected(true);

        // Enviar ping a cada 30 segundos para manter conexão viva
        pingInterval.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send('ping');
          }
        }, 30000);
      };

      ws.current.onmessage = (event) => {
        console.log('[WebSocket] 📬 Raw message event received:', {
          data: event.data,
          timestamp: new Date().toLocaleTimeString()
        });
        
        try {
          const data = JSON.parse(event.data);
          console.log('[WebSocket] 📨 Message parsed successfully:', {
            type: data.type,
            hasData: !!data.data,
            dataKeys: data.data ? Object.keys(data.data) : [],
            fullMessage: data
          });
          
          console.log('[WebSocket] 🔍 Checking if should call onMessage...');
          console.log('  - data.type:', data.type);
          console.log('  - is pong?', data.type === 'pong');
          console.log('  - onMessage exists?', !!onMessage);
          console.log('  - onMessage type:', typeof onMessage);
          
          if (data.type !== 'pong') {
            console.log('[WebSocket] ✅ Not a pong, should call onMessage');
            if (onMessage) {
              console.log('[WebSocket] 🔔 Calling onMessage callback with:', data.type);
              onMessage(data);
              console.log('[WebSocket] ✅ onMessage callback executed successfully');
            } else {
              console.error('[WebSocket] ❌ onMessage callback is undefined or null!');
            }
          } else {
            console.log('[WebSocket] ⏭️ Skipping pong message');
          }
        } catch (error) {
          console.error('[WebSocket] ❌ Error parsing message:', error, 'Raw data:', event.data);
        }
      };

      ws.current.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
      };

      ws.current.onclose = (event) => {
        console.log('[WebSocket] Disconnected:', event.code, event.reason);
        setIsConnected(false);

        // Limpar ping interval
        if (pingInterval.current) {
          clearInterval(pingInterval.current);
          pingInterval.current = null;
        }

        // Tentar reconectar após 3 segundos
        if (enabled) {
          reconnectTimeout.current = setTimeout(() => {
            console.log('[WebSocket] Attempting to reconnect...');
            connect();
          }, 3000);
        }
      };
    } catch (error) {
      console.error('[WebSocket] Connection error:', error);
    }
  }, [enabled, onMessage]);

  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }

    if (pingInterval.current) {
      clearInterval(pingInterval.current);
      pingInterval.current = null;
    }

    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }

    setIsConnected(false);
  }, []);

  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return { isConnected, disconnect };
}
